/**
 * Step 1 · Podstawowe informacje — the name, the type, and what a set measures.
 *
 * Two columns, the specimen's `1.06fr 1fr`: the name and six type tiles on the left, nine metric
 * rows on the right.
 *
 * ── The type tiles are NEUTRAL and the glyph carries the hue ────────────────────────────────
 * Owner decision D9: six washed cards read as decoration, one coloured drawing per tile encodes the
 * same thing quietly. The tile is a hairline on the card surface; chosen, it is an ink border over
 * `--bg-inset`. A `radiogroup` with a roving tab stop — one Tab reaches the group, the arrows move
 * the choice — because that is what a radio group is to a keyboard.
 *
 * ── The metric rows are the phone's FULL titles ─────────────────────────────────────────────
 * D10: `createWizard.category.measurement.*`, title and description, never the set table's
 * abbreviations. Each row is a toggle (`aria-pressed`); a row a type filled in carries „auto z
 * kategorii" until the trainer touches it. A type change leaves a note with „Cofnij", which puts
 * back what was there (`model/metricsDefaults`).
 *
 * ── On an EDIT the rows are frozen, and they say so ─────────────────────────────────────────
 * R11: metrics are fixed once the exercise exists, and `PatchExerciseRequest` cannot carry them. The
 * nine rows still stand — the box keeps its size and the trainer can read what the movement
 * measures — but they are `aria-disabled` and described by the phone's own sentence about why.
 * `aria-disabled` rather than the attribute, so the explanation stays in the tab order.
 */

import { useId, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ExerciseTypeGlyph, exerciseTypeColour } from '~/entities/exercise-library';
import { ALL_MEASUREMENT_FIELD_KEYS, measurementFieldDefinition } from '~/shared/measurement';
import { CheckGlyph, ChevronDownGlyph } from '~/shared/ui/FormGlyphs';

import type { CreateExerciseState } from '../lib/useCreateExercise';
import { metricsRequired } from '../model/draftValidation';
import { DRAFT_LIMITS } from '../model/exerciseDraft';
import { CREATABLE_EXERCISE_TYPES, type CreatableExerciseType } from '../model/exerciseType';
import { metricRowKeys, metricsPreview, metricsPreviewLine } from '../model/metricsPreview';

import styles from './CreateExerciseDialog.module.css';

export function StepBasics({ wizard }: { readonly wizard: CreateExerciseState }) {
  return (
    <div className={`${String(styles.cols)} ${String(styles.colsBasics)}`}>
      <div className={styles.col}>
        <NameField wizard={wizard} />
        <TypeTiles wizard={wizard} />
      </div>
      <div className={styles.col}>
        <MetricRows wizard={wizard} />
      </div>
    </div>
  );
}

function RequiredMark() {
  const { t } = useTranslation();
  return <span className={styles.required}>{t('web:exerciseLibrary.creator.required')}</span>;
}

function NameField({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const id = useId();
  const counterId = useId();

  return (
    <div className={styles.block}>
      <label className={styles.label} htmlFor={id}>
        {t('web:exerciseLibrary.creator.name.label')}
        <RequiredMark />
      </label>
      <input
        id={id}
        className={styles.input}
        type="text"
        value={wizard.draft.name}
        maxLength={DRAFT_LIMITS.name}
        autoComplete="off"
        placeholder={t('web:exerciseLibrary.creator.name.placeholder')}
        aria-describedby={counterId}
        data-blocker="name"
        onChange={(event) => {
          wizard.dispatch({ type: 'setName', name: event.target.value });
        }}
      />
      <span id={counterId} className={styles.counter}>
        {t('web:exerciseLibrary.creator.name.counter', {
          count: wizard.draft.name.length,
          max: DRAFT_LIMITS.name,
        })}
      </span>
    </div>
  );
}

function TypeTiles({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const labelId = useId();
  const tiles = useRef<(HTMLButtonElement | null)[]>([]);

  // On an edit the reducer keeps the metrics frozen (R11); the tile only ever says which type.
  const choose = (type: CreatableExerciseType) => {
    wizard.dispatch({ type: 'setType', exerciseType: type });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const count = CREATABLE_EXERCISE_TYPES.length;
    const at = CREATABLE_EXERCISE_TYPES.indexOf(wizard.draft.type);
    const target =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? (at + 1) % count
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? (at - 1 + count) % count
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? count - 1
              : null;
    if (target === null) return;
    event.preventDefault();
    const type = CREATABLE_EXERCISE_TYPES[target];
    if (type === undefined) return;
    choose(type);
    tiles.current[target]?.focus();
  };

  return (
    <div className={styles.block}>
      <span className={styles.label} id={labelId}>
        {t('web:exerciseLibrary.creator.type.label')}
        <RequiredMark />
      </span>
      <div className={styles.types} role="radiogroup" aria-labelledby={labelId} onKeyDown={onKeyDown}>
        {CREATABLE_EXERCISE_TYPES.map((type, index) => {
          const checked = wizard.draft.type === type;
          return (
            <button
              key={type}
              ref={(element) => {
                tiles.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className={styles.type}
              style={exerciseTypeColour(type)}
              onClick={() => {
                choose(type);
              }}
            >
              <ExerciseTypeGlyph type={type} className={styles.typeGlyph} />
              <span className={styles.typeName}>{t(`web:exerciseLibrary.type.${type}`)}</span>
              <span className={styles.typeDesc}>
                {t(`web:exerciseLibrary.creator.type.description.${type}`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MetricRows({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t, i18n } = useTranslation();
  const labelId = useId();
  const lockedId = useId();
  const helpId = useId();
  const locked = wizard.draft.metricsFrozen;
  const rows = useRef<HTMLDivElement>(null);
  const { metrics } = wizard.draft;

  const preview = metricsPreviewLine(
    metricsPreview(metrics.fields).map((part) => t(part.labelKey)),
    i18n.language,
  );

  return (
    <div className={styles.block}>
      <span className={styles.label} id={labelId}>
        {t('web:exerciseLibrary.creator.metrics.label')}
        {metricsRequired(wizard.draft) && <RequiredMark />}
      </span>

      {/* On an edit, WHY the rows below do not answer is said before them, not after (polish of
        * 4 November 2026): read first, it frames every row as a record rather than a control. */}
      {locked && (
        <p id={lockedId} className={styles.lockNote}>
          {metrics.fields.length === 0
            ? t('web:exerciseLibrary.creator.metrics.lockedEmpty')
            : t('web:exerciseLibrary.creator.metrics.locked')}
        </p>
      )}

      {!locked && metrics.autoNote !== null && (
        <div className={styles.autoNote}>
          <span>
            {t('web:exerciseLibrary.creator.metrics.autoNote', {
              type: t(`web:exerciseLibrary.type.${metrics.autoNote}`),
            })}
          </span>
          {metrics.undo !== null && (
            <button
              type="button"
              className={styles.textButton}
              onClick={() => {
                wizard.dispatch({ type: 'undoMetrics' });
                // „Cofnij" unmounts with its note; focus goes to the rows it just changed rather
                // than falling to the popup and restarting the Tab order at the rail.
                rows.current?.querySelector<HTMLElement>('button')?.focus();
              }}
            >
              {t('web:exerciseLibrary.creator.metrics.undo')}
            </button>
          )}
        </div>
      )}

      <div ref={rows} className={styles.mets} role="group" aria-labelledby={labelId}>
        {ALL_MEASUREMENT_FIELD_KEYS.map((field, index) => {
          const on = metrics.fields.includes(field);
          const auto = on && metrics.auto.includes(field);
          const keys = metricRowKeys(field);
          const unit = measurementFieldDefinition(field).unit;
          return (
            <button
              key={field}
              type="button"
              className={`${String(styles.met)} ${locked ? String(styles.metLocked) : ''}`}
              aria-pressed={on}
              data-blocker={index === 0 ? 'metrics' : undefined}
              aria-disabled={locked ? 'true' : undefined}
              aria-describedby={locked ? lockedId : undefined}
              onClick={() => {
                if (!locked) wizard.dispatch({ type: 'toggleMetric', field });
              }}
            >
              <span className={styles.metBox} aria-hidden>
                <CheckGlyph className={styles.metBoxGlyph} />
              </span>
              <span>
                <span className={styles.metTitle}>
                  {t(keys.titleKey)}
                  {auto && (
                    <span className={styles.auto}>
                      {t('exerciseLibrary.createWizard.category.autoFromCategory')}
                    </span>
                  )}
                </span>
                <span className={styles.metDesc}>{t(keys.descriptionKey)}</span>
              </span>
              {unit === null ? <span /> : <span className={styles.metUnit}>{unit}</span>}
            </button>
          );
        })}
      </div>

      <div className={styles.rule} />

      <p className={styles.preview}>
        {t('web:exerciseLibrary.creator.metrics.previewLabel')}
        <b className={styles.previewValue}>
          {preview === '' ? t('web:exerciseLibrary.creator.metrics.previewEmpty') : preview}
        </b>
      </p>

      {!locked && (
        <div className={styles.info}>
          <button
            type="button"
            className={styles.infoButton}
            aria-expanded={wizard.metricsHelpOpen}
            aria-controls={helpId}
            onClick={wizard.toggleMetricsHelp}
          >
            <span className={styles.infoMark} aria-hidden>
              i
            </span>
            {t('web:exerciseLibrary.creator.metrics.helpToggle')}
            <ChevronDownGlyph className={styles.infoChevron} />
          </button>
          <p id={helpId} className={styles.frozen} hidden={!wizard.metricsHelpOpen}>
            {t('web:exerciseLibrary.creator.metrics.help')}
          </p>
        </div>
      )}
    </div>
  );
}
